import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { Button, Checkbox } from "@mui/joy";
import { Avatar, Box, Divider, Paper, Popover, Tooltip } from "@mui/material";
import {
  DataGridPremium as DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridActionsCellItem,
  GridPastedValueParser,
  GridRowId,
  GridRowSelectionModel,
  GridRenderCellParams,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  useGridApiContext,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { useConfirm } from "material-ui-confirm";
import { useEffect, useMemo, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch, useSelector } from "react-redux";
import {
  defaultDataGridProps,
  injectDefaultColumnProps,
} from "../../constants";
import { Cue, Cue_Entity } from "../../entities/cue";
import { Talent_Entity } from "../../entities/talent";
import {
  durationFormatter,
  durationToSec,
  getWidthFromLocalStorage,
  greyStyleForNonEditableCells,
  renderDurationInputCell,
} from "../../helpers/dataGridHelpers";
import { setFormOpen } from "../../redux/appStatus/appStatusActions";
// import { setBulkSelect, setSelectedGridCues } from "../../redux/cue/cueActions";
import {
  useDeleteCueMutation,
  useUpdateBatchCuesMutation,
  useUpdateCueMutation,
} from "../../redux/cue/cueEndpoints";
import { useCues } from "../../redux/cue/cueHooks";
import { selectedProjectID } from "../../redux/filmsReducer";
import { useProject } from "../../redux/project/projectHooks";
import { useTalents } from "../../redux/talent/talentHooks";
import NoRowsOverlay from "../noRowsOverlay/noRowsOverlay";
import { usePendingEdits } from "../pendingEdits/pendingEdits";
import { useSaveError } from "../saveError/saveError";
import { SelectTalentInputCell } from "../selectTalents/selectTalents";
import { CueBatch } from "./cueBatch";
import CueForm from "./cueForm/cueForm";
import TalentAvatar from "../talent/talentsList/talentAvatar";

type CueOptionKey =
  | "showInMPS"
  | "showInInvoice"
  | "showInContract"
  | "includeInCalculation";

const cueOptionKeys: CueOptionKey[] = [
  "showInMPS",
  "showInInvoice",
  "showInContract",
  "includeInCalculation",
];

const emptyRowSelectionModel = (): GridRowSelectionModel => ({
  type: "include",
  ids: new Set<GridRowId>(),
});

// Clearing a number cell yields "" / null / undefined. `Number(undefined)` is
// NaN, which serialises to null and makes the backend reject the whole cue —
// the edit silently bounces back and has to be retyped.
const toCueNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const emptyCueOptions = (): Record<CueOptionKey, boolean> => ({
  showInMPS: false,
  showInInvoice: false,
  showInContract: false,
  includeInCalculation: false,
});

const cueOptionsToClipboardValue = (row: Cue_Entity): string => {
  const tokens: string[] = [];
  if (row.showInMPS) tokens.push("M");
  if (row.showInInvoice) tokens.push("I");
  if (row.showInContract) tokens.push("C");
  if (row.includeInCalculation) tokens.push("+");
  return tokens.join(",");
};

const parseCueOptionsFromClipboard = (
  value: string
): Record<CueOptionKey, boolean> => {
  const parsed = emptyCueOptions();
  const text = value?.trim().toLowerCase();
  if (!text) return parsed;

  if (["all", "true", "1", "yes", "y"].includes(text)) {
    cueOptionKeys.forEach((key) => {
      parsed[key] = true;
    });
    return parsed;
  }

  if (["none", "false", "0", "no", "n"].includes(text)) {
    return parsed;
  }

  const tokens = text.split(/[,\s;|/]+/).filter(Boolean);
  const collapsed = text.replace(/[^mic+]/g, "");

  const mark = (token: string) => {
    if (token === "m" || token === "showinmps") parsed.showInMPS = true;
    if (token === "i" || token === "showininvoice") parsed.showInInvoice = true;
    if (token === "c" || token === "showincontract")
      parsed.showInContract = true;
    if (token === "+" || token === "includeincalculation") {
      parsed.includeInCalculation = true;
    }
  };

  tokens.forEach(mark);
  collapsed.split("").forEach(mark);

  return parsed;
};

/**
 * Cue
 *
 * @returns {null} Cue
 */
export default function Cues() {
  const projectID = useSelector(selectedProjectID);

  const { project } = useProject(projectID);
  const { cues, cuesMap } = useCues({
    filters: JSON.stringify([
      {
        name: "cues.projectID",
        value: projectID,
        comparison: "eq",
      },
    ]),
  });
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();
  const { talents } = useTalents();
  const confirm = useConfirm();

  const [bulkSelect, setBulkSelect] = useState(false);
  const [selectedGridCues, setSelectedGridCues] =
    useState<GridRowSelectionModel>(emptyRowSelectionModel);

  const [deleteCue] = useDeleteCueMutation();
  const [updateCue] = useUpdateCueMutation();
  const [updateBatchCues] = useUpdateBatchCuesMutation();
  const { reportSaveError, saveErrorSnackbar } = useSaveError();
  const {
    pendingEdits,
    pendingValue,
    markPending,
    dropPending,
    releaseSettled,
  } = usePendingEdits();

  // Fresh server data: let go of the cells it has caught up with.
  useEffect(() => {
    releaseSettled((id, field) => cuesMap?.[Number(id)]?.[field]);
  }, [cuesMap, releaseSettled]);

  // Overlay the values committed but not yet echoed back, so the refetch that
  // an earlier row's save triggered can't revert them.
  const rowsWithPendingEdits = useMemo(
    () =>
      (cues ?? []).map((cue) => {
        const pendingForCue = Object.keys(pendingEdits).filter((key) =>
          key.startsWith(`${cue.id}:`)
        );
        if (!pendingForCue.length) return cue;
        const row = { ...cue } as Cue_Entity;
        pendingForCue.forEach((key) => {
          const field = key.slice(key.indexOf(":") + 1);
          row[field] = pendingValue(cue.id, field, row[field]);
        });
        return row;
      }),
    [cues, pendingEdits, pendingValue]
  );

  const parseTalentIDsFromClipboard: GridPastedValueParser<
    Cue_Entity,
    number[]
  > = (value) => {
    if (!value) return [];
    return value
      .split(/[,\s;|/]+/)
      .map((v) => Number(v.trim()))
      .filter((v) => Number.isFinite(v) && v > 0);
  };

  const parseCueOptionsClipboard: GridPastedValueParser<Cue_Entity, any> = (
    value
  ) => parseCueOptionsFromClipboard(value);

  const parseDurationFromClipboard: GridPastedValueParser<
    Cue_Entity,
    number | undefined
  > = (value) => {
    const text = value?.trim();
    if (!text) return undefined;

    const seconds = durationToSec(text);
    if (seconds >= 0) return seconds;

    const numeric = Number(text);
    if (Number.isFinite(numeric)) return Math.round(numeric);

    return undefined;
  };

  const onCueUpdate = async (_newCue: Cue_Entity, _oldCue: Cue_Entity) => {
    if (!_newCue?.id) {
      throw new Error("Cue update failed: missing row id");
    }

    const body: Partial<Cue_Entity> = {};
    for (const key in _newCue) {
      if (
        Object.prototype.hasOwnProperty.call(_newCue, key) &&
        _oldCue[key] !== _newCue[key]
      ) {
        body[key] = _newCue[key];
      }
    }

    const selectedCueIDs: GridRowId[] = Array.from(
      apiRef.current.getSelectedRows().keys()
    );

    // Hold the committed fields until the server reports them, so the refetch
    // triggered by an earlier row's save can't revert this one.
    const heldIDs: GridRowId[] = selectedCueIDs.length
      ? selectedCueIDs
      : [_newCue.id];
    const heldFields = Object.keys(body);
    heldIDs.forEach((id) =>
      heldFields.forEach((field) => markPending(id, field, body[field]))
    );

    // Deliberately not awaited: holding the cell in edit mode for a round-trip
    // swallows whatever is typed next, so entering a column of numbers at speed
    // lost everything after the first value.
    saveCues(selectedCueIDs, body, _newCue).catch((error) => {
      heldIDs.forEach((id) =>
        heldFields.forEach((field) => dropPending(id, field))
      );
      reportSaveError(error);
    });

    return { ..._newCue };
  };

  const saveCues = async (
    selectedCueIDs: GridRowId[],
    body: Partial<Cue_Entity>,
    _newCue: Cue_Entity
  ): Promise<void> => {
    const batch: Partial<Cue_Entity>[] = [];

    if (selectedCueIDs.length) {
      for (const cueID of selectedCueIDs) {
        const cue = cuesMap?.[cueID as keyof typeof cuesMap];
        if (cue) batch.push({ ...cue, ...body });
      }
      if (batch.length > 0) {
        await updateBatchCues(batch).unwrap();
        return;
      }
    }

    // Backend expects the full cue payload for single-row updates.
    await updateCue({ id: _newCue.id, body: _newCue }).unwrap();
  };

  const columns = injectDefaultColumnProps([
    {
      field: "id",
      headerName: "ID",
      type: "string",
      sortable: false,
      valueGetter: (_value: any, row: Cue_Entity) =>
        cues ? cues.findIndex((c) => c.id === row?.id) + 1 : 0,
      width: getWidthFromLocalStorage("cue", "id", 30),
    },
    {
      field: "code",
      headerName: "Cue",
      editable: true,
      sortable: false,
      type: "string",
      width: getWidthFromLocalStorage("cue", "code", 100),
      headerAlign: "center",
    },
    {
      field: "version",
      headerName: "Ver.",
      editable: true,
      sortable: false,
      width: getWidthFromLocalStorage("cue", "version", 80),
      type: "string",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "qualifier",
      headerName: "Qua.",
      editable: true,
      sortable: false,
      width: getWidthFromLocalStorage("cue", "qualifier", 60),
      type: "string",
      headerAlign: "center",
      align: "center",
    },
    {
      field: "title",
      headerName: "Title",
      editable: true,
      sortable: false,
      width: getWidthFromLocalStorage("cue", "title", 200),
      type: "string",
      headerAlign: "center",
    },
    {
      field: "length",
      headerName: "Duration",
      editable: true,
      sortable: false,
      type: "number",
      width: getWidthFromLocalStorage("cue", "length", 80),
      headerAlign: "center",
      pastedValueParser: parseDurationFromClipboard,
      valueFormatter: (valueOrParams: any) => {
        const value = valueOrParams?.value ?? valueOrParams;
        return durationFormatter(value);
      },
      renderEditCell: renderDurationInputCell,
    },
    {
      field: "bars",
      headerName: "Bars",
      editable: true,
      sortable: false,
      type: "number",
      valueSetter: (value: any, row: Cue_Entity) => ({
        ...row,
        bars: toCueNumber(value),
      }),
      width: getWidthFromLocalStorage("cue", "bars", 100),
      align: "center",
      headerAlign: "center",
    },
    {
      field: "lines",
      headerName: "Lines",
      editable: true,
      sortable: false,
      type: "number",
      valueSetter: (value: any, row: Cue_Entity) => ({
        ...row,
        lines: toCueNumber(value),
      }),
      width: getWidthFromLocalStorage("cue", "lines", 100),
      align: "center",
      headerAlign: "center",
    },
    {
      field: "talentIDs",
      headerName: "Orchestrators",
      editable: true,
      sortable: false,
      width: getWidthFromLocalStorage("cue", "talentIDs", 200),
      headerAlign: "center",
      pastedValueParser: parseTalentIDsFromClipboard,
      valueFormatter: (valueOrParams: any) => {
        const value = valueOrParams?.value ?? valueOrParams;
        if (!Array.isArray(value)) return "";
        return value.join(",");
      },
      valueSetter: (value: any, row: Cue_Entity) => ({
        ...row,
        talentIDs: Array.isArray(value) ? value : [],
      }),
      renderEditCell: (p) => (
        <SelectTalentInputCell
          id={p.id}
          field={p.field}
          value={p.value}
          talents={talents ?? []}
        />
      ),
      renderCell: (p) => {
        const _talents: Talent_Entity[] = [];
        (p.value ?? []).forEach((t: number) => {
          const talent = talents?.find((e) => e.id === t);
          if (talent) _talents.push(talent);
        });

        return (
          <Box sx={{ display: "flex", gap: 1 }}>
            {_talents
              .sort((a, b) => a.lastName.localeCompare(b.lastName))
              .map((t: Talent_Entity) => {
                return (
                  <Tooltip key={t.id} title={`${t?.firstName} ${t?.lastName}`}>
                    <TalentAvatar talent={t} size={22} active={t.active} />
                  </Tooltip>
                );
              })}
          </Box>
        );
      },
    },
    {
      field: "notes",
      headerName: "Notes",
      editable: true,
      sortable: false,
      type: "string",
      flex: 1,
      width: getWidthFromLocalStorage("cue", "notes", 400),
      headerAlign: "center",
    },
    {
      field: "options",
      headerName: "Options",
      type: "string",
      editable: true,
      sortable: false,
      cellClassName: "actions",
      valueGetter: (_value: any, row: Cue_Entity) =>
        cueOptionsToClipboardValue(row),
      pastedValueParser: parseCueOptionsClipboard,
      valueSetter: (value: any, row: Cue_Entity) => {
        const parsed =
          typeof value === "string"
            ? parseCueOptionsFromClipboard(value)
            : value;
        if (!parsed || typeof parsed !== "object") return row;
        return {
          ...row,
          showInMPS: !!parsed.showInMPS,
          showInInvoice: !!parsed.showInInvoice,
          showInContract: !!parsed.showInContract,
          includeInCalculation: !!parsed.includeInCalculation,
        };
      },
      renderCell: (p: GridRenderCellParams) => (
        <CueOptions
          values={{
            showInContract: p.row.showInContract,
            showInInvoice: p.row.showInInvoice,
            showInMPS: p.row.showInMPS,
            includeInCalculation: p.row.includeInCalculation,
          }}
        />
      ),
      renderEditCell: (p) => <CueOptionsEdit p={p} onCueUpdate={onCueUpdate} />,
      width: 100,
      headerAlign: "center",
    },
    {
      field: "action",
      type: "actions",
      headerName: "Actions",
      width: getWidthFromLocalStorage("cue", "action", 50),
      cellClassName: "actions",
      getActions: ({ row }: { row: Cue_Entity }) => [
        <GridActionsCellItem
          key="delete"
          disabled={project?.locked()}
          icon={<DeleteIcon />}
          label={`Delete`}
          showInMenu
          onClick={() => {
            confirm({
              description: `You are about to delete a cue`,
            })
              .then(() => {
                deleteCue(row.id);
              })
              .catch(() => {
                /* ... */
              });
          }}
          color="inherit"
        />,
      ],
    },
  ]);

  const grid = useMemo(() => {
    return (
      <Paper
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <DataGridPro
          {...defaultDataGridProps}
          rowSelection={bulkSelect}
          isCellEditable={(params) =>
            project?.locked() ? false : params.colDef.editable ?? false
          }
          apiRef={apiRef}
          getCellClassName={(p) => {
            let classes = greyStyleForNonEditableCells(p);
            if (project?.locked()) classes += " lock";
            return classes;
          }}
          onColumnWidthChange={(p) =>
            localStorage.setItem(`cue_${p.colDef.field}_width`, `${p.width}`)
          }
          onRowSelectionModelChange={(p) => setSelectedGridCues(p)}
          rowSelectionModel={selectedGridCues}
          checkboxSelection={bulkSelect}
          processRowUpdate={onCueUpdate}
          onProcessRowUpdateError={reportSaveError}
          rows={rowsWithPendingEdits}
          columns={columns}
          slotProps={{
            cell: { tabIndex: 1 },
            footer: { rows: cues },
            toolbar: { setSelectedGridCues, setBulkSelect, bulkSelect },
          }}
          initialState={{
            pinnedColumns: {
              left: [
                GRID_CHECKBOX_SELECTION_COL_DEF.field,
                "id",
                "code",
                "version",
                "qualifier",
              ],
              right: ["options", "action"],
            },
          }}
          showToolbar
          slots={{
            toolbar: CueToolbar,
            noRowsOverlay: NoRowsOverlay,
            footer: CueFooter,
          }}
        />
        <CueForm />
      </Paper>
    );
  }, [rowsWithPendingEdits, cues, bulkSelect, selectedGridCues]);

  return (
    <>
      {grid}
      {saveErrorSnackbar}
    </>
  );
}

function CueToolbar({ setSelectedGridCues, setBulkSelect, bulkSelect }: any) {
  const dispatch = useDispatch();
  const projectID = useSelector(selectedProjectID);
  const { project } = useProject(projectID);

  const handleNewCue = () => {
    if (!project?.locked()) dispatch(setFormOpen(true, "cueForm"));
  };

  useHotkeys(`ctrl+enter`, handleNewCue, {
    enableOnTags: ["INPUT", "SELECT", "TEXTAREA"],
  });

  return (
    <>
      <CueBatch />
      <Toolbar>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
            <Tooltip title="ctrl + enter">
              <Button
                size="sm"
                variant="soft"
                onClick={handleNewCue}
                disabled={project?.locked()}
              >
                + New Cue
              </Button>
            </Tooltip>
            <Button
              size="sm"
              variant="plain"
              onClick={() => dispatch(setFormOpen(true, "cueBatch"))}
              disabled={project?.locked()}
            >
              <i className="fa-solid fa-line-columns pright"></i> Batch Import
            </Button>
            <QuickFilter
              expanded
              style={{ flex: 1, display: "flex", alignItems: "center" }}
            >
              <QuickFilterControl
                size="small"
                fullWidth
                placeholder="Search..."
              />
              <QuickFilterClear />
            </QuickFilter>
          </Box>
          <Button
            size="sm"
            variant={bulkSelect ? "solid" : "plain"}
            onClick={() => {
              setSelectedGridCues(emptyRowSelectionModel());
              setBulkSelect((e) => !e);
            }}
            disabled={project?.locked()}
          >
            {bulkSelect ? "Done" : "Bulk Edit"}
          </Button>
        </Box>
      </Toolbar>
    </>
  );
}

function CueFooter({ rows }: { rows: Cue_Entity[] }) {
  let totalLength = 0;

  for (const key in rows) {
    if (Object.prototype.hasOwnProperty.call(rows, key)) {
      const row = rows[key];
      totalLength += row.length;
    }
  }
  return (
    <>
      <Divider />
      <Box
        sx={{
          p: 1,
          display: "flex",
          fontWeight: 600,
          gap: 1,
          alignItems: "center",
        }}
      >
        <span>{rows.length} Starts</span>
        <Divider
          sx={{ height: 14 }}
          orientation="vertical"
          flexItem
          variant="middle"
        />
        <span>Total: {durationFormatter(totalLength)}</span>
      </Box>
    </>
  );
}

const options: CueOptionKey[] = cueOptionKeys;
const labelForOptions = {
  showInMPS: "Show in Music Prep Summary",
  showInInvoice: "Show in individual Invoices",
  showInContract: "Show in the Contract",
  includeInCalculation: "Included in Calculation",
};
const negationLabelForOptions = {
  showInMPS: "NOT showing in Music Prep Summary",
  showInInvoice: "NOT showing in individual Invoices",
  showInContract: "NOT showing in the Contract",
  includeInCalculation: "Excluded from Calculation",
};
const letterForOption = {
  showInMPS: "M",
  showInInvoice: "I",
  showInContract: "C",
  includeInCalculation: "+",
};

function CueOptions({
  values,
}: {
  values: {
    showInMPS: boolean;
    showInInvoice: boolean;
    showInContract: boolean;
    includeInCalculation: boolean;
  };
}) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        width: 100,
        justifyContent: "center",
      }}
    >
      {options.map((o) => {
        const checked = values[o];
        return (
          <Tooltip
            key={o}
            title={!checked ? negationLabelForOptions[o] : labelForOptions[o]}
          >
            <Box
              sx={{
                background: checked ? "rgba(76, 175, 80, 0.3)" : "#ffe082",
                color: checked ? "rgba(76, 175, 80, 1)" : "#f57c00",
                width: 14,
                height: 14,
                borderRadius: 1,
                fontWeight: 800,
                fontSize: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {letterForOption[o]}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

function CueOptionsEdit({
  p,
  onCueUpdate,
}: {
  p: GridRenderCellParams;
  onCueUpdate: (
    _newCue: Cue_Entity,
    _oldCue: Cue_Entity
  ) => Promise<Cue_Entity>;
}) {
  const ref = useRef();
  const cue: Cue = p.row;
  const apiRef = useGridApiContext();

  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (ref.current) setOpen(true);
  }, [ref]);

  const [values, setValues] = useState({
    showInMPS: cue.showInMPS,
    showInInvoice: cue.showInInvoice,
    showInContract: cue.showInContract,
    includeInCalculation: cue.includeInCalculation,
  });

  const onClose = () => {
    onCueUpdate({ ...p.row, ...values }, { ...p.row });
    apiRef.current.stopCellEditMode({
      id: p.id,
      field: p.field,
      ignoreModifications: true,
    });
  };

  return (
    <>
      <Box ref={ref}>
        <CueOptions values={values} />
      </Box>
      <Popover
        id={"simple-popover"}
        open={open}
        anchorEl={ref.current}
        onClose={onClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box sx={{ p: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          {options.map((o) => {
            const checked = values[o];
            return (
              <Checkbox
                sx={{
                  "&:hover": {
                    background: "rgba(155,155,155,0.1)",
                  },
                }}
                variant={checked ? "soft" : "solid"}
                color={checked ? "success" : "warning"}
                size="sm"
                key={o}
                onChange={(e) =>
                  setValues((v) => {
                    const _v = { ...v };
                    _v[o] = e.target.checked;
                    return _v;
                  })
                }
                checked={values[o]}
                label={`${labelForOptions[o]} (${letterForOption[o]})`}
              />
            );
          })}
        </Box>
      </Popover>
    </>
  );
}
